import React, { createContext, useState, useEffect } from 'react';
import { getCurrencyForCountry } from '../utils/countryUtils';
import { buildApiUrl } from '../config/environment';

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [currentAgent, setCurrentAgent] = useState(null); // Kept for backward compatibility
  const [personas, setPersonas] = useState([]);
  const [activePersona, setActivePersona] = useState(null);
  const [agencyId, setAgencyId] = useState(null);
  const [agencyCountry, setAgencyCountry] = useState('AU');
  const [agencyCurrency, setAgencyCurrency] = useState('AUD');
  const [token, setToken] = useState(null);
  const [activeSubscription, setActiveSubscription] = useState(null);

  // Derive subscription from the active persona whenever it changes
  useEffect(() => {
    setActiveSubscription(activePersona?.subscription ?? null);
  }, [activePersona]);

  // Load data from session storage on component mount
  useEffect(() => {
    const storedUser = sessionStorage.getItem('user');
    const storedAgent = sessionStorage.getItem('currentAgent');
    const storedPersonas = sessionStorage.getItem('personas');
    const storedActivePersona = sessionStorage.getItem('activePersona');
    const storedAgencyId = sessionStorage.getItem('agencyId');
    const storedCountry = sessionStorage.getItem('agencyCountry');
    const storedCurrency = sessionStorage.getItem('agencyCurrency');
    const storedToken = sessionStorage.getItem('token');

    if (storedUser) {
      setUser(JSON.parse(storedUser));
    }

    // Restore personas (new persona-based auth)
    if (storedPersonas) {
      setPersonas(JSON.parse(storedPersonas));
    }
    if (storedActivePersona) {
      const persona = JSON.parse(storedActivePersona);
      setActivePersona(persona);
      setAgencyId(persona.entity_id);
      setAgencyCountry(persona.country || 'AU');
      setAgencyCurrency(persona.currency || 'AUD');
    } else if (storedAgent) {
      // Fallback to old agent-based auth for backward compatibility
      setCurrentAgent(JSON.parse(storedAgent));
    }

    if (storedAgencyId && !storedActivePersona) {
      setAgencyId(parseInt(storedAgencyId));
    }
    if (storedCountry && !storedActivePersona) {
      setAgencyCountry(storedCountry);
    }
    if (storedCurrency && !storedActivePersona) {
      setAgencyCurrency(storedCurrency);
    }
    if (storedToken) {
      setToken(storedToken);
    }
  }, []);

  const login = (loginResponse) => {
    console.log('[AuthContext] login called with:', loginResponse);

    // Store JWT tokens
    if (loginResponse.tokens) {
      const { access_token, refresh_token } = loginResponse.tokens;

      if (access_token) {
        setToken(access_token);
        sessionStorage.setItem('token', access_token);
        sessionStorage.setItem('access_token', access_token);
        console.log('[AuthContext] access_token stored in sessionStorage');
      } else {
        console.warn('[AuthContext] No access_token in loginResponse.tokens');
      }

      if (refresh_token) {
        sessionStorage.setItem('refresh_token', refresh_token);
        console.log('[AuthContext] refresh_token stored in sessionStorage');
      } else {
        console.warn('[AuthContext] No refresh_token in loginResponse.tokens');
      }
    } else {
      console.warn('[AuthContext] No tokens in loginResponse');
    }

    // Store user data
    if (loginResponse.user) {
      setUser(loginResponse.user);
      sessionStorage.setItem('user', JSON.stringify(loginResponse.user));
      console.log('[AuthContext] user stored in sessionStorage:', loginResponse.user);

      // Handle NEW persona-based auth (Phase 0)
      if (loginResponse.user.personas && loginResponse.user.personas.length > 0) {
        setPersonas(loginResponse.user.personas);
        sessionStorage.setItem('personas', JSON.stringify(loginResponse.user.personas));
        console.log('[AuthContext] personas stored:', loginResponse.user.personas);

        // Auto-select default persona
        const defaultPersona = loginResponse.user.personas.find(
          p => p.persona_id === loginResponse.user.default_persona_id
        ) || loginResponse.user.personas[0];

        setActivePersona(defaultPersona);
        setAgencyId(defaultPersona.entity_id);
        setAgencyCountry(defaultPersona.country || 'AU');
        setAgencyCurrency(defaultPersona.currency || getCurrencyForCountry(defaultPersona.country || 'AU'));

        // Store in session storage
        sessionStorage.setItem('activePersona', JSON.stringify(defaultPersona));
        sessionStorage.setItem('agencyId', defaultPersona.entity_id.toString());
        sessionStorage.setItem('agencyCountry', defaultPersona.country || 'AU');
        sessionStorage.setItem('agencyCurrency', defaultPersona.currency || getCurrencyForCountry(defaultPersona.country || 'AU'));
        console.log('[AuthContext] Active persona selected:', defaultPersona.name, 'entity_id:', defaultPersona.entity_id);
      }
      // Fallback to OLD agent-based auth (backward compatibility)
      else if (loginResponse.user.agents && loginResponse.user.agents.length > 0) {
        const firstAgent = loginResponse.user.agents[0];
        const agency = firstAgent.agency;

        setCurrentAgent(firstAgent);
        setAgencyId(agency.id);
        setAgencyCountry(agency.country || 'AU');
        setAgencyCurrency(agency.currency || getCurrencyForCountry(agency.country || 'AU'));

        // Store in session storage
        sessionStorage.setItem('currentAgent', JSON.stringify(firstAgent));
        sessionStorage.setItem('agencyId', agency.id.toString());
        sessionStorage.setItem('agencyCountry', agency.country || 'AU');
        sessionStorage.setItem('agencyCurrency', agency.currency || getCurrencyForCountry(agency.country || 'AU'));
        console.log('[AuthContext] agencyId, agencyCountry and agencyCurrency stored (agent mode):', agency.id, agency.country, agency.currency);
      } else {
        // Clear agency data if no agents or personas
        setCurrentAgent(null);
        setPersonas([]);
        setActivePersona(null);
        setAgencyId(null);
        setAgencyCountry('AU');
        setAgencyCurrency('AUD');
        sessionStorage.removeItem('currentAgent');
        sessionStorage.removeItem('personas');
        sessionStorage.removeItem('activePersona');
        sessionStorage.removeItem('agencyId');
        sessionStorage.removeItem('agencyCountry');
        sessionStorage.removeItem('agencyCurrency');
        console.warn('[AuthContext] No agents or personas found in user');
      }
    } else {
      console.warn('[AuthContext] No user in loginResponse');
    }
  };

  const logout = () => {
    setUser(null);
    setCurrentAgent(null);
    setPersonas([]);
    setActivePersona(null);
    setAgencyId(null);
    setAgencyCountry('AU');
    setAgencyCurrency('AUD');
    setToken(null);

    // Clear all session storage including tokens
    sessionStorage.removeItem('user');
    sessionStorage.removeItem('currentAgent');
    sessionStorage.removeItem('personas');
    sessionStorage.removeItem('activePersona');
    sessionStorage.removeItem('agencyId');
    sessionStorage.removeItem('agencyCountry');
    sessionStorage.removeItem('agencyCurrency');
    sessionStorage.removeItem('token');
    sessionStorage.removeItem('access_token');
    sessionStorage.removeItem('refresh_token');
  };

  const switchPersona = async (personaId) => {
    try {
      const response = await fetch(
        buildApiUrl(`/personas/${personaId}/switch`),
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to switch persona');
      }

      const data = await response.json();

      // Update token with new persona context
      if (data.tokens?.access_token) {
        setToken(data.tokens.access_token);
        sessionStorage.setItem('token', data.tokens.access_token);
        sessionStorage.setItem('access_token', data.tokens.access_token);
      }

      // Update active persona
      const newActivePersona = personas.find(p => p.persona_id === personaId);
      if (newActivePersona) {
        setActivePersona(newActivePersona);
        setAgencyId(newActivePersona.entity_id);
        setAgencyCountry(newActivePersona.country || 'AU');
        setAgencyCurrency(newActivePersona.currency || 'AUD');

        sessionStorage.setItem('activePersona', JSON.stringify(newActivePersona));
        sessionStorage.setItem('agencyId', newActivePersona.entity_id.toString());
        sessionStorage.setItem('agencyCountry', newActivePersona.country || 'AU');
        sessionStorage.setItem('agencyCurrency', newActivePersona.currency || 'AUD');

        console.log('[AuthContext] Switched to persona:', newActivePersona.name);
      }

      return data;
    } catch (error) {
      console.error('[AuthContext] Failed to switch persona:', error);
      throw error;
    }
  };

  const register = (userData) => {
    setUser(userData);
    sessionStorage.setItem('user', JSON.stringify(userData));
  };

  // Helper function to get auth headers for API calls
  const getAuthHeaders = () => {
    const headers = {
      'Content-Type': 'application/json',
    };

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    return headers;
  };

  return (
    <AuthContext.Provider value={{
      user,
      setUser,
      currentAgent,        // Kept for backward compatibility
      personas,            // NEW: Array of all personas
      activePersona,       // NEW: Currently active persona
      activeSubscription,  // NEW: Subscription for the active entity
      subscriptionPlan: activeSubscription?.plan ?? 'free', // NEW: Shortcut string
      agencyId,            // Derived from activePersona.entity_id for back-compat
      agencyCountry,       // Derived from activePersona.country for back-compat
      agencyCurrency,      // Derived from activePersona.currency for back-compat
      token,
      login,
      logout,
      switchPersona,       // NEW: Function to switch between personas
      register,
      getAuthHeaders,
      setAgencyCurrency
    }}>
      {children}
    </AuthContext.Provider>
  );
};